import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import CrmTabs from "../crm/CrmTabs";
import AdminAppReviewService from "./AdminAppReviewService";
import { getAppReviewColumns } from "./getAppReviewColumns";

export default function AdminAppReviewMain() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [totalRows, setTotalRows] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [refreshKey, setRefreshKey] = useState(0);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [removingId, setRemovingId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const result = await AdminAppReviewService.list(paginationModel.page, paginationModel.pageSize);
            if (cancelled) return;
            setLoading(false);
            if (result.success && result.data) {
                const data = result.data;
                const content = data.content ?? data;
                const list = Array.isArray(content) ? content : [];
                setRows(list);
                const total = data.page?.totalElements ?? data.totalElements ?? list.length;
                setTotalRows(typeof total === "number" ? total : list.length);
            } else {
                setRows([]);
                setTotalRows(0);
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Failed to load review prompts",
                });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [paginationModel.page, paginationModel.pageSize, refreshKey]);

    const handleConfirmRemove = useCallback(async (id) => {
        if (!id) return;
        setRemovingId(id);
        const result = await AdminAppReviewService.remove(id);
        setRemovingId(null);
        setRemoveTarget(null);
        const failed = !result.success || result.data?.code === "FAILURE";
        if (failed) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.data?.message || result.error || "Failed to remove review prompt",
            });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Review prompt removed" });
        setRefreshKey((current) => current + 1);
    }, []);

    const removeLabel = [removeTarget?.name, removeTarget?.email].filter(Boolean).join(" ") || "this prompt";

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <CrmTabs />
            <Typography variant="h5" sx={{ mb: 2 }}>
                Review
            </Typography>
            <Box sx={{ width: "100%", minHeight: 400 }}>
                <DataGrid
                    rows={rows}
                    columns={getAppReviewColumns(navigate, {
                        onRemove: setRemoveTarget,
                        removingId,
                    })}
                    getRowId={(row) => row.id}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    autoHeight
                    sx={{ minHeight: 400 }}
                />
            </Box>
            <ConfirmDialog
                open={Boolean(removeTarget)}
                setOpen={(open) => {
                    if (!removingId && !open) setRemoveTarget(null);
                }}
                title="Remove review prompt?"
                message="This clears the saved prompt so the app can ask for a review again."
                targetName={removeLabel}
                targetId={removeTarget?.id}
                okText="Remove"
                cancelText="Cancel"
                callback={handleConfirmRemove}
            />
            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((current) => ({ ...current, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
